import { useMemo, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { ASSET_CLASS_BY_ID } from "@/constants/asset-classes";
import { Layout } from "@/components/Layout";
import { useI18n } from "@/i18n/use-i18n";
import { getIntlLocale } from "@/i18n/locale";
import { assetClassesService, fiiAnalysisService, searchAssetsService, stockAnalysisService, targetAssetsService } from "@/services";
import { AddAssetDialog } from "@/pages/TargetPortfolio/components/AddAssetDialog";
import { FiiAnalysisSection } from "./components/FiiAnalysisSection";
import { StockAnalysisSection } from "./components/StockAnalysisSection";
import { AssetClassCode } from "@/types/assets";
import { fiiAnalysisQueryKeys } from "@/resources/apis/fii-analysis";
import { stockAnalysisQueryKeys } from "@/resources/apis/stock-analysis";
import "./market-details.css";
import { Asset } from "@/types/assets";

const emptyValue = "--";

export default function MarketDetails() {
  const { locale, strings } = useI18n();
  const intlLocale = getIntlLocale(locale);
  const { ticker } = useParams<{ ticker: string }>();

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 2,
      }),
    [intlLocale]
  );

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        maximumFractionDigits: 2,
      }),
    [intlLocale]
  );

  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [intlLocale]
  );

  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const normalizedTicker = (ticker ?? "").trim().toUpperCase();
  const assetQuery = useQuery<Asset>({
    queryKey: ["search-asset", normalizedTicker],
    queryFn: () => searchAssetsService.getByTicker(normalizedTicker),
    enabled: normalizedTicker.length > 0,
  });
  const asset = assetQuery.data ?? null;

  const classesQuery = useQuery({
    queryKey: ['asset-classes'],
    queryFn: assetClassesService.list,
  });

  const classOptions = useMemo(() => {
    return (classesQuery.data ?? []).map((cls) => {
      const definition = ASSET_CLASS_BY_ID[cls.code];
      const copy = strings.assetClassesCatalog[cls.code as AssetClassCode];
      return {
        id: cls.id,
        code: cls.code as AssetClassCode,
        name: copy?.name ?? definition?.name ?? cls.code.toUpperCase(),
      };
    });
  }, [classesQuery.data, strings.assetClassesCatalog]);

  const isFii = asset?.type === AssetClassCode.FII
  const isStock = asset?.type === AssetClassCode.ACAO

  const fiiAnalysisQuery = useQuery({
    queryKey: fiiAnalysisQueryKeys.detail(normalizedTicker),
    queryFn: () => fiiAnalysisService.getByTicker(normalizedTicker),
    enabled: isFii && normalizedTicker.length > 0,
    staleTime: 1000 * 60 * 60,
  })

  const stockAnalysisQuery = useQuery({
    queryKey: stockAnalysisQueryKeys.detail(normalizedTicker),
    queryFn: () => stockAnalysisService.getByTicker(normalizedTicker),
    enabled: isStock && normalizedTicker.length > 0,
    staleTime: 1000 * 60 * 60,
  })

  const fiiData = fiiAnalysisQuery.data ?? null
  const stockData = stockAnalysisQuery.data ?? null

  const pvp = isStock ? stockData?.pvp ?? null : fiiData?.pvp ?? null
  const dyMonthly = fiiData?.dyPct ?? null
  const roe = stockData?.roePct ?? null
  const netMargin = stockData?.netMarginPct ?? null
  const debtEbitda = stockData?.debtEbitda ?? null

  const createAssetMutation = useMutation({
    mutationFn: targetAssetsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['target-portfolio'] });
      setIsAddDialogOpen(false);
    },
  });

  const formatSignedPercent = (value?: number) => {
    if (value === undefined) return emptyValue;
    const sign = value > 0 ? "+" : "";
    return `${sign}${percentFormatter.format(value)}%`;
  };

  const formatSignedNumber = (value?: number) => {
    if (value === undefined) return emptyValue;
    const sign = value > 0 ? "+" : "";
    return `${sign}${numberFormatter.format(value)}`;
  };

  const formatNumber = (value?: number) =>
    value === undefined ? emptyValue : numberFormatter.format(value);

  const formatCurrency = (value?: number) =>
    value === undefined ? emptyValue : currencyFormatter.format(value);
  const fundamentalsLabels = strings.market.details.fundamentals.labels;
  const comingSoonLabel = strings.market.details.fundamentals.comingSoon;
  const website = asset?.summary_profile?.website?.trim() ?? "";
  const websiteUrl = website
    ? /^https?:\/\//i.test(website)
      ? website
      : `https://${website}`
    : "";

  const isLoadingAsset =
    assetQuery.isLoading || (assetQuery.isFetching && !asset);
  const queryErrorMessage = assetQuery.isError
    ? assetQuery.error instanceof Error
      ? assetQuery.error.message
      : strings.market.list.error
    : "";

  if (isLoadingAsset) {
    return (
      <Layout>
        <section className="market-details app-page">
          <header className="market-details__header">
            <div className="market-details__titles">
              <h1>{strings.market.details.title}</h1>
            </div>
          </header>
          <div className="app-state app-state--loading market-details__empty">
            <p>{strings.market.list.loading}</p>
          </div>
        </section>
      </Layout>
    );
  }

  if (queryErrorMessage) {
    return (
      <Layout>
        <section className="market-details app-page">
          <header className="market-details__header">
            <div className="market-details__titles">
              <h1>{strings.market.details.title}</h1>
            </div>
          </header>
          <div className="app-state app-state--error market-details__empty">
            <p>{queryErrorMessage}</p>
            <Link to="/market" className="market-details__link">
              {strings.market.details.backToMarket}
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  if (!asset) {
    return (
      <Layout>
        <section className="market-details app-page">
          <header className="market-details__header">
            <div className="market-details__titles">
              <h1>{strings.market.details.title}</h1>
            </div>
          </header>
          <div className="app-state app-state--error market-details__empty">
            <p>{strings.market.details.notFound}</p>
            <Link to="/market" className="market-details__link">
              {strings.market.details.backToMarket}
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  const classDefinition = ASSET_CLASS_BY_ID[asset.type];
  const classLabel = asset.type
    ? strings.assetClassesCatalog[asset.type]?.name ??
      classDefinition?.name ??
      asset.type.toUpperCase()
    : undefined;
  const classColor = classDefinition?.color ?? "var(--color-brand)";
  const changeValue = asset.regular_market_change_percent;
  const changeAbsolute = asset.regular_market_change;
  const changeToneSource = changeValue ?? changeAbsolute;
  const changeTone =
    changeToneSource === undefined
      ? "neutral"
      : changeToneSource >= 0
      ? "positive"
      : "negative";
  const formattedChangeAbsolute = formatSignedNumber(changeAbsolute);
  const formattedChangePercent = formatSignedPercent(changeValue);
  const changeSummary =
    formattedChangeAbsolute !== emptyValue &&
    formattedChangePercent !== emptyValue
      ? `${formattedChangeAbsolute} (${formattedChangePercent})`
      : formattedChangeAbsolute !== emptyValue
      ? formattedChangeAbsolute
      : formattedChangePercent !== emptyValue
      ? formattedChangePercent
      : emptyValue;
  /*  const investedValue = asset.position
    ? asset.position.quantity * asset.position.averagePrice
    : 0;
  const hasPosition = asset.position && asset.position.quantity > 0;*/

  return (
    <Layout>
      <section className="market-details app-page">
        <header className="market-details__header">
          <div className="market-details__titles">
            <h1>{strings.market.details.title}</h1>
          </div>
          <Link to="/market" className="market-details__back">
            <ChevronLeft size={16} />
            {strings.market.details.back}
          </Link>
        </header>

        <div className="market-details__grid">
          <div className="market-details__main">
            <div className="market-details__summary">
              <div className="market-details__summary-head">
                <div className="market-details__identity">
                  {asset.logo_url ? (
                    <img
                      className="market-details__logo"
                      src={asset.logo_url}
                      alt={asset.ticker}
                      loading="lazy"
                    />
                  ) : (
                    <span
                      className="market-details__logo market-details__logo--placeholder"
                      aria-hidden="true"
                    >
                      {asset.ticker.slice(0, 1)}
                    </span>
                  )}
                  <div>
                    <div className="market-details__ticker-row">
                      <span className="market-details__ticker">
                        {asset.ticker}
                      </span>
                      {classLabel ? (
                        <span
                          className="market-details__badge market-details__badge--tone"
                          style={
                            { "--badge-color": classColor } as CSSProperties
                          }
                        >
                          {classLabel}
                        </span>
                      ) : null}
                      {/*asset.inWallet ? (
                        <span
                          className="market-details__badge market-details__badge--status"
                          style={{ '--badge-color': 'var(--color-brand)' } as CSSProperties}
                        >
                          {strings.market.list.inWallet}
                        </span>
                      ) : null*/}
                    </div>
                    <p className="market-details__name">{asset.long_name?.trim() || asset.short_name?.trim() || asset.ticker}</p>
                    {websiteUrl ? (
                      <a
                        className="market-details__website"
                        href={websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {strings.market.details.website}
                      </a>
                    ) : null}
                  </div>
                </div>
                <div className="market-details__price">
                  <strong>{formatCurrency(asset.price)}</strong>
                  <div
                    className={`market-details__variation-line market-details__variation-line--${changeTone}`}
                  >
                    {changeSummary}
                  </div>
                </div>
              </div>

              <div className="market-details__actions">
                <button
                  type="button"
                  className="market-details__button market-details__button--primary"
                >
                  {strings.market.details.actions.simulate}
                </button>
                <button
                  type="button"
                  className="market-details__button"
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  {strings.market.details.actions.addWallet}
                </button>
              </div>
            </div>

            <div className="market-details__card">
              <div className="market-details__card-header">
                <div>
                  <h2>{strings.market.details.fundamentals.title}</h2>
                  <p>{strings.market.details.fundamentals.subtitle}</p>
                </div>
              </div>
              <div className="market-details__stats">
                <div className="market-details__stat">
                  <span>{fundamentalsLabels.pl}</span>
                  <strong>{asset.price_earnings ? formatNumber(asset.price_earnings) : emptyValue}</strong>
                </div>
                <div className="market-details__stat">
                  <span>{fundamentalsLabels.pvp}</span>
                  <strong>{pvp != null ? formatNumber(pvp) : comingSoonLabel}</strong>
                </div>
                <div className="market-details__stat">
                  <span>{isFii ? 'DY Mensal' : fundamentalsLabels.dy}</span>
                  <strong>{dyMonthly != null ? `${percentFormatter.format(dyMonthly)}%` : comingSoonLabel}</strong>
                </div>
                <div className="market-details__stat">
                  <span>{fundamentalsLabels.roe}</span>
                  <strong>{roe != null ? `${percentFormatter.format(roe)}%` : comingSoonLabel}</strong>
                </div>
                <div className="market-details__stat">
                  <span>{fundamentalsLabels.netMargin}</span>
                  <strong>{netMargin != null ? `${percentFormatter.format(netMargin)}%` : comingSoonLabel}</strong>
                </div>
                <div className="market-details__stat">
                  <span>{fundamentalsLabels.debtEbitda}</span>
                  <strong>{debtEbitda != null ? `${formatNumber(debtEbitda)}x` : comingSoonLabel}</strong>
                </div>
              </div>
            </div>

            <div className="market-details__card">
              <div className="market-details__card-header">
                <h2>{strings.market.details.auxiliary.title}</h2>
              </div>
              <div className="market-details__stats">
                <div className="market-details__stat">
                  <span>
                    {strings.market.details.auxiliary.earningsPerShare}
                  </span>
                  <strong>{asset.earnings_per_shared ? formatNumber(asset.earnings_per_shared) : emptyValue}</strong>
                </div>
                <div className="market-details__stat">
                  <span>{strings.market.details.auxiliary.marketCap}</span>
                  <strong>{formatCurrency(asset.market_cap)}</strong>
                </div>
              </div>
            </div>

            <div className="market-details__card">
              <div className="market-details__card-header">
                <h2>{strings.market.details.chart.title}</h2>
              </div>
              <div className="market-details__chart">
                {strings.market.details.chart.description}
              </div>
            </div>

            {isFii && (
              <FiiAnalysisSection ticker={normalizedTicker} data={fiiData ?? undefined} />
            )}
            {isStock && (
              <StockAnalysisSection ticker={normalizedTicker} data={stockData ?? undefined} />
            )}
          </div>

          <aside className="market-details__aside">
            <div className="market-details__card">
              <div className="market-details__card-header">
                <h2>{strings.market.details.quickActions.title}</h2>
              </div>
              <div className="market-details__actions-list">
                <button
                  type="button"
                  className="market-details__button market-details__button--primary"
                >
                  {strings.market.details.quickActions.simulate}
                </button>
                <button type="button" className="market-details__button">
                  {strings.market.details.quickActions.compare}
                </button>
              </div>
            </div>

          </aside>
        </div>
      </section>
      <AddAssetDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        classes={classOptions}
        onSubmit={(data) => createAssetMutation.mutate(data)}
        isSubmitting={createAssetMutation.isPending}
        initialTicker={normalizedTicker}
      />
    </Layout>
  );
}
